import * as React from 'react';
import { WorkspaceButton } from '@/components/ui/workspace';
import { formatDay, plural } from '@/components/discovery/discovery-logic';
import {
  saveQuestionnaireDraft,
  submitQuestionnaire,
  type AnswerValue,
  type QuestionnairePayload,
} from '@/lib/api/secure-links';
import { ClientCard } from './client-frame';
import { QuestionnaireForm } from './questionnaire-form';
import { answeredValues } from './secure-link-logic';
import { DoneCard, problemText, type LinkPageProps } from './secure-link-parts';

/**
 * The questionnaire from a secure link (E12-F08; prototype
 * `#client-questionnaire`). Answers save as you go (a draft on the run,
 * not evidence) and become evidence only when sent (Decision 54.27); the
 * thank-you page says they have been received, not that anyone will
 * review them. A department link reads the same, for its department.
 */

/** Wait this long after the last change before saving. */
export const SAVE_DELAY_MS = 700;

type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

export function ClientQuestionnaire({
  token,
  session,
  payload,
  onInvalid,
}: LinkPageProps & { payload: QuestionnairePayload }) {
  const ids = React.useMemo(() => payload.questions.map((q) => q.run_question_id), [payload]);
  const [values, setValues] = React.useState<Record<string, AnswerValue>>(() =>
    answeredValues(payload.draft_values, ids),
  );
  const [save, setSave] = React.useState<SaveState>('idle');
  const [sending, setSending] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState<number | null>(
    session.completed ? payload.answered : null,
  );

  // Saves run one at a time, each with the latest answers, so an older save
  // never lands after a newer one.
  const latest = React.useRef(values);
  latest.current = values;
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const busy = React.useRef(false);
  const again = React.useRef(false);
  const finished = React.useRef(sent !== null);

  const flush = React.useCallback(async () => {
    if (busy.current) {
      again.current = true;
      return;
    }
    busy.current = true;
    setSave('saving');
    try {
      do {
        again.current = false;
        await saveQuestionnaireDraft(token, answeredValues(latest.current, ids));
      } while (again.current && !finished.current);
      if (!finished.current) setSave('saved');
    } catch (err) {
      if (finished.current) return;
      if (onInvalid(err)) return;
      setSave('failed');
    } finally {
      busy.current = false;
    }
  }, [token, ids, onInvalid]);

  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const answer = (key: string, value: AnswerValue | undefined) => {
    setProblem(null);
    setValues((prev) => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), SAVE_DELAY_MS);
  };

  const send = async () => {
    const answers = answeredValues(values, ids);
    if (Object.keys(answers).length === 0) {
      setProblem('Answer at least one question before sending.');
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    setSending(true);
    setProblem(null);
    try {
      // Every question goes, unanswered ones as null, so an answer cleared
      // since the last save is not sent from that save.
      const res = await submitQuestionnaire(
        token,
        Object.fromEntries(ids.map((id) => [id, answers[id] ?? null])),
      );
      finished.current = true;
      setSent(res.answered);
    } catch (err) {
      if (!onInvalid(err)) setProblem(problemText(err, 'Your answers could not be sent.'));
    } finally {
      setSending(false);
    }
  };

  const first = session.person_first_name;
  if (sent !== null) {
    return (
      <DoneCard title={first ? `Thank you, ${first}` : 'Thank you'}>
        Your {plural(sent, 'answer')} {sent === 1 ? 'has' : 'have'} been received.
      </DoneCard>
    );
  }

  const meta = [session.client_name, payload.audience_line].filter(Boolean).join(' · ');
  if (payload.questions.length === 0) {
    return (
      <ClientCard>
        <div className="text-meta text-ink-500">{meta}</div>
        <h1 className="my-1 text-h1 font-semibold text-ink-900">{payload.title}</h1>
        <p className="text-body text-ink-700">
          Every question on this link has already been answered. There is nothing more to do.
        </p>
      </ClientCard>
    );
  }

  const answered = Object.keys(answeredValues(values, ids)).length;
  return (
    <ClientCard>
      <QuestionnaireForm
        meta={meta}
        title={payload.title}
        minutes={payload.minutes}
        until={`until ${formatDay(session.expires_at)}`}
        questions={payload.questions.map((q) => ({ ...q, key: q.run_question_id }))}
        values={values}
        answered={answered}
        onAnswer={answer}
        footer={
          <div className="mt-2 flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
            <span
              aria-live="polite"
              className={
                save === 'failed' ? 'text-meta text-posture-critical' : 'text-meta text-ink-500'
              }
            >
              {save === 'saving'
                ? 'Saving…'
                : save === 'saved'
                  ? 'Saved'
                  : save === 'failed'
                    ? 'Not saved. Your answers are still here; check your connection.'
                    : ''}
            </span>
            {problem ? (
              <span role="alert" className="text-meta text-posture-critical">
                {problem}
              </span>
            ) : null}
            <WorkspaceButton
              size="md"
              variant="primary"
              disabled={sending}
              onClick={() => void send()}
            >
              {sending ? 'Sending…' : 'Send my answers'}
            </WorkspaceButton>
          </div>
        }
      />
    </ClientCard>
  );
}
